import React, { useState, useEffect, useRef } from "react";
import { Users, ZoomIn, ZoomOut, RotateCw, X, Grid3X3, DoorOpen } from "lucide-react";
import { RestaurantTable, Order } from "../types";
import { supabase } from "../lib/supabase";

interface FloorplanItem {
  id: string;
  type: "table" | "bar" | "kitchen" | "entrance" | "till" | "wall" | "counter";
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  shape?: "circle" | "square" | "rectangle";
  table?: RestaurantTable;
  label?: string;
  color?: string;
}

interface FloorplanTablePickerProps {
  onTableSelect: (table: RestaurantTable) => void;
  onClose: () => void;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function makeNoTable(): RestaurantTable {
  return {
    id: "no-table",
    table_number: 0,
    seats: 0,
    status: "available",
    x_position: 0,
    y_position: 0,
    created_at: "",
  } as RestaurantTable;
}

const FloorplanTablePicker: React.FC<FloorplanTablePickerProps> = ({ onTableSelect, onClose }) => {
  const canvasRef = useRef<HTMLDivElement>(null);

  const [floorplanItems, setFloorplanItems] = useState<FloorplanItem[]>([]);
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);

  const [zoom, setZoom] = useState<number>(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    fetchTables();
    fetchOrders();
    loadFloorplan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (floorplanItems.length > 0 && !isInitialized) {
      autoFitFloorplan();
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorplanItems, isInitialized]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom((prev) => Math.max(0.3, Math.min(3, prev * delta)));
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener("wheel", handleWheel, { passive: false });
      return () => canvas.removeEventListener("wheel", handleWheel as any);
    }
  }, []);

  const fetchTables = async () => {
    try {
      const { data, error } = await supabase.from("restaurant_tables").select("*").order("table_number");

      if (error) {
        console.error("Error fetching tables:", error);
        setTables([
          { id: "1", table_number: 1, seats: 4, status: "available", x_position: 100, y_position: 100, created_at: "" },
          { id: "2", table_number: 2, seats: 2, status: "occupied", x_position: 150, y_position: 150, created_at: "" },
          { id: "3", table_number: 3, seats: 4, status: "available", x_position: 100, y_position: 200, created_at: "" },
          { id: "4", table_number: 4, seats: 6, status: "reserved", x_position: 200, y_position: 200, created_at: "" },
          { id: "5", table_number: 5, seats: 2, status: "available", x_position: 300, y_position: 100, created_at: "" },
          { id: "6", table_number: 6, seats: 8, status: "cleaning", x_position: 400, y_position: 200, created_at: "" },
        ] as any);
      } else {
        setTables((data || []) as any);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .neq("status", "completed")
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching orders:", error);
        setOrders([]);
      } else {
        setOrders((data || []) as any);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const autoFitFloorplan = () => {
    if (floorplanItems.length === 0) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    floorplanItems.forEach((item) => {
      minX = Math.min(minX, item.x);
      minY = Math.min(minY, item.y);
      maxX = Math.max(maxX, item.x + item.width);
      maxY = Math.max(maxY, item.y + item.height);
    });

    const padding = 50;
    const contentWidth = maxX - minX + padding * 2;
    const contentHeight = maxY - minY + padding * 2;

    const headerHeight = 120;
    const legendHeight = 96;
    const containerPadding = 40;

    const containerWidth = window.innerWidth - containerPadding;
    const containerHeight = window.innerHeight - headerHeight - legendHeight;

    const zoomX = containerWidth / contentWidth;
    const zoomY = containerHeight / contentHeight;
    const optimalZoom = Math.min(zoomX, zoomY, 2);

    const centerX =
      (containerWidth - contentWidth * optimalZoom) / 2 - (minX - padding) * optimalZoom + containerPadding / 2;
    const centerY = (containerHeight - contentHeight * optimalZoom) / 2 - (minY - padding) * optimalZoom;

    setZoom(optimalZoom);
    setPanOffset({ x: centerX, y: centerY });
  };

  const loadFloorplan = async () => {
    try {
      const { data, error } = await supabase
        .from("floorplan_items")
        .select(
          `
          *,
          restaurant_tables!floorplan_items_table_id_fkey (*)
        `
        )
        .order("created_at");

      if (error) {
        console.error("Error fetching floorplan:", error);
        createInitialFloorplan();
      } else if (data && data.length > 0) {
        const transformedItems: FloorplanItem[] = (data as any[]).map((item) => {
          if (item.type === "table" && item.restaurant_tables) {
            const hasOrder = orders.some((order: any) => order.table_number === item.restaurant_tables.table_number);
            const actualStatus = hasOrder ? "occupied" : item.restaurant_tables.status;
            return {
              id: item.item_id,
              type: item.type as any,
              x: item.x,
              y: item.y,
              width: item.width,
              height: item.height,
              rotation: item.rotation,
              shape: item.shape as any,
              table: { ...item.restaurant_tables, status: actualStatus },
              label: item.label,
              color: item.color,
            };
          }
          return {
            id: item.item_id,
            type: item.type as any,
            x: item.x,
            y: item.y,
            width: item.width,
            height: item.height,
            rotation: item.rotation,
            shape: item.shape as any,
            table: item.restaurant_tables,
            label: item.label,
            color: item.color,
          };
        });
        setFloorplanItems(transformedItems);
      } else {
        createInitialFloorplan();
      }
    } catch (error) {
      console.error("Error loading floorplan:", error);
      createInitialFloorplan();
    }
  };

  const createInitialFloorplan = () => {
    const tableItems: FloorplanItem[] = tables.map((table, index) => {
      const hasOrder = orders.some((order: any) => order.table_number === table.table_number);
      const actualStatus = hasOrder ? "occupied" : table.status;
      return {
        id: `table-${table.id}`,
        type: "table",
        x: (index % 5) * 120 + 100,
        y: Math.floor(index / 5) * 120 + 150,
        width: 96,
        height: 96,
        rotation: 0,
        shape: "circle",
        table: { ...table, status: actualStatus } as any,
      };
    });

    const roomItems: FloorplanItem[] = [
      { id: "entrance-1", type: "entrance", x: 50, y: 50, width: 140, height: 34, rotation: 0, label: "Entrance", color: "#10B981" },
      { id: "kitchen-1", type: "kitchen", x: 600, y: 50, width: 200, height: 130, rotation: 0, label: "Kitchen", color: "#EF4444" },
      { id: "bar-1", type: "bar", x: 300, y: 420, width: 220, height: 64, rotation: 0, label: "Bar", color: "#8B5CF6" },
    ];

    setFloorplanItems([...tableItems, ...roomItems]);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning) return;

    const deltaX = e.clientX - panStart.x;
    const deltaY = e.clientY - panStart.y;

    setPanOffset({
      x: panOffset.x + deltaX,
      y: panOffset.y + deltaY,
    });
    setPanStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => setIsPanning(false);

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.target === canvasRef.current || (e.target as HTMLElement).classList.contains("canvas-background")) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev / 1.2, 0.3));
  const resetView = () => autoFitFloorplan();

  const handleTableClick = (item: FloorplanItem) => {
    if (
      item.type === "table" &&
      item.table &&
      (item.table.status === "available" || item.table.status === "reserved")
    ) {
      onTableSelect(item.table);
    }
  };

  const handleNoTable = () => {
    onTableSelect(makeNoTable());
  };

  const tableStatusMeta = (status: string) => {
    switch (status) {
      case "available":
        return {
          ring: "ring-emerald-200",
          bg: "bg-emerald-50",
          dot: "bg-emerald-500",
          text: "text-emerald-800",
          badge: "bg-emerald-100 text-emerald-800",
        };
      case "reserved":
        return {
          ring: "ring-amber-200",
          bg: "bg-amber-50",
          dot: "bg-amber-500",
          text: "text-amber-800",
          badge: "bg-amber-100 text-amber-800",
        };
      case "occupied":
        return {
          ring: "ring-rose-200",
          bg: "bg-rose-50",
          dot: "bg-rose-500",
          text: "text-rose-800",
          badge: "bg-rose-100 text-rose-800",
        };
      case "cleaning":
      default:
        return {
          ring: "ring-slate-200",
          bg: "bg-slate-50",
          dot: "bg-slate-500",
          text: "text-slate-800",
          badge: "bg-slate-100 text-slate-800",
        };
    }
  };

  const getItemStyle = (item: FloorplanItem) => {
    const isTable = item.type === "table" && item.table;
    const tableStatus = isTable ? (item.table!.status as any) : null;
    const meta = tableStatus ? tableStatusMeta(tableStatus) : null;

    const selectable =
      isTable && (tableStatus === "available" || tableStatus === "reserved");

    const disabled =
      isTable && (tableStatus === "occupied" || tableStatus === "cleaning");

    const base: React.CSSProperties = {
      position: "absolute",
      left: item.x,
      top: item.y,
      width: item.width,
      height: item.height,
      transform: `rotate(${item.rotation}deg)`,
      transition: "transform 0.15s ease, box-shadow 0.15s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      userSelect: "none",
      zIndex: 1,
      cursor: selectable ? "pointer" : "default",
      opacity: disabled ? 0.45 : 1,
    };

    if (isTable) {
      // For light UI: white table cards with colored ring/badge
      // We use inline styles for position + size, and Tailwind classes for everything else on the element.
      return base;
    }

    return {
      ...base,
      opacity: 0.9,
    };
  };

  const getTableClass = (status: string, shape?: FloorplanItem["shape"]) => {
    const meta = tableStatusMeta(status);
    return cx(
      "group",
      "bg-white",
      "ring-2",
      meta.ring,
      "shadow-sm",
      "hover:shadow-md",
      "active:shadow-sm",
      "flex",
      "items-center",
      "justify-center",
      "text-slate-900",
      "select-none",
      shape === "circle" ? "rounded-full" : "rounded-2xl",
      status === "available" || status === "reserved"
        ? "hover:-translate-y-0.5"
        : ""
    );
  };

  const getRoomClass = () => {
    return cx(
      "bg-slate-900/70",
      "text-white",
      "rounded-2xl",
      "shadow-sm",
      "ring-1",
      "ring-white/15"
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 text-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm text-slate-500">POS</div>
          <h2 className="text-2xl font-extrabold tracking-tight">Select Table</h2>
          <p className="text-sm text-slate-500 mt-1">
            Click an <span className="font-semibold text-slate-700">available</span> or{" "}
            <span className="font-semibold text-slate-700">reserved</span> table to assign to this order.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={handleNoTable}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-slate-900 text-white font-extrabold shadow-sm hover:bg-slate-800 transition-colors"
          >
            <DoorOpen size={18} />
            No Table
          </button>

          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-slate-100 text-slate-900 font-extrabold ring-1 ring-slate-200 hover:bg-slate-200 transition-colors"
          >
            <X size={18} />
            Close
          </button>

          <div className="hidden md:flex items-center gap-2 bg-slate-100 ring-1 ring-slate-200 rounded-2xl p-1.5">
            <button
              onClick={handleZoomOut}
              className="p-2 rounded-xl text-slate-700 hover:bg-white hover:shadow-sm transition-colors"
              title="Zoom out"
            >
              <ZoomOut size={18} />
            </button>

            <div className="px-2 min-w-[72px] text-center font-extrabold tabular-nums text-slate-900">
              {Math.round(zoom * 100)}%
            </div>

            <button
              onClick={handleZoomIn}
              className="p-2 rounded-xl text-slate-700 hover:bg-white hover:shadow-sm transition-colors"
              title="Zoom in"
            >
              <ZoomIn size={18} />
            </button>

            <button
              onClick={resetView}
              className="p-2 rounded-xl text-slate-700 hover:bg-white hover:shadow-sm transition-colors"
              title="Reset view"
            >
              <RotateCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 min-h-0 p-6">
        <div
          ref={canvasRef}
          className={cx(
            "relative w-full h-full rounded-3xl",
            "bg-white",
            "ring-1 ring-slate-200",
            "shadow-sm overflow-hidden",
            "cursor-grab active:cursor-grabbing"
          )}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onMouseDown={handleCanvasMouseDown}
        >
          {/* Zoomable container */}
          <div
            className="absolute inset-0"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
              transformOrigin: "0 0",
              width: "2000px",
              height: "1500px",
            }}
          >
            {/* Grid background */}
            <div
              className="canvas-background absolute inset-0 opacity-100"
              style={{
                backgroundImage: `
                  radial-gradient(circle at 1px 1px, rgba(15,23,42,0.08) 1px, transparent 0),
                  radial-gradient(circle at 1px 1px, rgba(15,23,42,0.05) 1px, transparent 0)
                `,
                backgroundSize: "28px 28px, 56px 56px",
              }}
            />

            {/* Items */}
            {floorplanItems.map((item) => {
              const isTable = item.type === "table" && item.table;
              if (isTable) {
                const status = item.table!.status as any;
                const meta = tableStatusMeta(status);
                const selectable = status === "available" || status === "reserved";

                return (
                  <div
                    key={item.id}
                    style={getItemStyle(item)}
                    className={getTableClass(status, item.shape)}
                    onClick={() => handleTableClick(item)}
                    onMouseDown={(e) => e.stopPropagation()}
                    role={selectable ? "button" : undefined}
                    aria-disabled={!selectable}
                  >
                    <div className="text-center px-2">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl font-extrabold tracking-tight tabular-nums">
                          {item.table!.table_number}
                        </span>
                        <span
                          className={cx(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-extrabold capitalize",
                            meta.badge
                          )}
                        >
                          <span className={cx("h-2 w-2 rounded-full", meta.dot)} />
                          {status}
                        </span>
                      </div>

                      <div className="mt-1 flex items-center justify-center text-sm font-semibold text-slate-600">
                        <Users size={14} className="mr-1.5 text-slate-500" />
                        {item.table!.seats}
                      </div>

                      <div
                        className={cx(
                          "mt-2 text-xs font-semibold",
                          selectable ? "text-slate-500" : "text-slate-400"
                        )}
                      >
                        {selectable ? "Tap to select" : "Unavailable"}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={item.id}
                  style={getItemStyle(item)}
                  className={getRoomClass()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="text-center px-3">
                    <div className="text-sm font-extrabold">{item.label}</div>
                    <div className="text-[11px] text-white/70 mt-0.5 flex items-center justify-center gap-1">
                      <Grid3X3 size={12} />
                      Area
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white border-t border-slate-200 px-6 py-4">
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm font-semibold text-slate-700">
          {[
            { label: "Available", dot: "bg-emerald-500", ring: "ring-emerald-200", clickable: true },
            { label: "Reserved", dot: "bg-amber-500", ring: "ring-amber-200", clickable: true },
            { label: "Occupied", dot: "bg-rose-500", ring: "ring-rose-200", clickable: false, faded: true },
            { label: "Cleaning", dot: "bg-slate-500", ring: "ring-slate-200", clickable: false, faded: true },
          ].map((s) => (
            <div
              key={s.label}
              className={cx(
                "inline-flex items-center gap-2 rounded-2xl px-3 py-2 bg-slate-50 ring-1 ring-slate-200",
                s.faded ? "opacity-70" : ""
              )}
            >
              <span className={cx("h-3 w-3 rounded-full", s.dot)} />
              <span>{s.label}</span>
              <span className="text-slate-400">•</span>
              <span className="text-slate-500">{s.clickable ? "Selectable" : "Unavailable"}</span>
            </div>
          ))}

          <div className="hidden md:inline-flex items-center gap-2 rounded-2xl px-3 py-2 bg-slate-50 ring-1 ring-slate-200 text-slate-600">
            <ZoomIn size={16} className="text-slate-500" />
            Ctrl + Scroll to zoom • Drag to pan
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorplanTablePicker;
